import React, { useState, useEffect, useMemo, useCallback, memo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { codeRepoAPI, assignmentAPI } from '../services/api';
import MemoizedSyntaxHighlighter from '../components/MemoizedSyntaxHighlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';

function StudentRepoProgress() {
  const { repoId, studentId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [markingComplete, setMarkingComplete] = useState(null);
  const [deletingSubmission, setDeletingSubmission] = useState(null);
  const [expandedAssignments, setExpandedAssignments] = useState(new Set());

  useEffect(() => {
    if (user && user.role !== 'admin') {
      setError('Access denied. Admin only.');
      navigate('/code-repos');
      return;
    }
    fetchStudentProgress();
  }, [repoId, studentId, user, navigate]);

  const fetchStudentProgress = async () => {
    try {
      setLoading(true);
      const response = await codeRepoAPI.getStudentRepoProgress(repoId, studentId);
      setData(response.data);
      setError('');
    } catch (err) {
      console.error('Error fetching student progress:', err);
      setError(err.response?.data?.message || 'Failed to fetch student progress');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = useCallback((status) => {
    switch (status) {
      case 'completed':
        return { bg: '#d1fae5', text: '#065f46', label: 'Completed' };
      case 'in-progress':
        return { bg: '#dbeafe', text: '#1e40af', label: 'In Progress' };
      case 'not-started':
        return { bg: '#f3f4f6', text: '#6b7280', label: 'Not Started' };
      default:
        return { bg: '#f3f4f6', text: '#6b7280', label: 'Unknown' };
    }
  }, []);

  const getLevelColor = (level) => {
    switch (level?.toLowerCase()) {
      case 'beginner':
        return '#22c55e';
      case 'intermediate':
        return '#f59e0b';
      case 'advanced':
        return '#ef4444';
      default:
        return '#718096';
    }
  };

  const formatTimeTaken = (startDate, endDate) => {
    if (!startDate || !endDate) return null;
    
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffMs = end - start;
    
    if (diffMs <= 0) return 'Less than a minute';
    
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    const parts = [];
    if (days > 0) parts.push(`${days}d`);
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);
    
    return parts.length > 0 ? parts.join(' ') : 'Less than a minute';
  };

  const handleMarkComplete = async (submissionId) => {
    if (!window.confirm('Are you sure you want to mark this assignment as complete?')) {
      return;
    }

    try {
      setMarkingComplete(submissionId);
      await assignmentAPI.markSubmissionComplete(submissionId);
      
      // Refresh the data
      await fetchStudentProgress();
      
      alert('Assignment marked as complete successfully!');
    } catch (err) {
      console.error('Error marking assignment as complete:', err);
      alert(err.response?.data?.message || 'Failed to mark assignment as complete');
    } finally {
      setMarkingComplete(null);
    }
  };

  const handleDeleteSubmission = async (submissionId) => {
    if (!window.confirm('Are you sure you want to delete this submission? This action cannot be undone.')) {
      return;
    }

    try {
      setDeletingSubmission(submissionId);
      await assignmentAPI.deleteSubmission(submissionId);
      
      // Refresh the data
      await fetchStudentProgress();
      
      alert('Submission deleted successfully!');
    } catch (err) {
      console.error('Error deleting submission:', err);
      alert(err.response?.data?.message || 'Failed to delete submission');
    } finally {
      setDeletingSubmission(null);
    }
  };

  const toggleAssignmentExpanded = useCallback((assignmentId) => {
    setExpandedAssignments(prev => {
      const newSet = new Set(prev);
      if (newSet.has(assignmentId)) {
        newSet.delete(assignmentId);
      } else {
        newSet.add(assignmentId);
      }
      return newSet;
    });
  }, []);

  if (loading) {
    return (
      <div className="container" style={{ paddingTop: '20px' }}>
        <div className="card">
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <p style={{ color: '#667eea', fontSize: '16px' }}>Loading student progress...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container" style={{ paddingTop: '20px' }}>
        <div className="card">
          <div className="error">{error}</div>
          <button 
            onClick={() => navigate(`/code-repos/${repoId}/progress`)}
            className="btn btn-secondary"
          >
            ← Back to Progress
          </button>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="container" style={{ paddingTop: '20px' }}>
        <div className="card">
          <p>No data available</p>
          <button 
            onClick={() => navigate(`/code-repos/${repoId}/progress`)}
            className="btn btn-secondary"
          >
            ← Back to Progress
          </button>
        </div>
      </div>
    );
  }

  const { repo, student, summary, assignmentProgress } = data;

  return (
    <div className="container" style={{ paddingTop: '20px', background: '#ffffff', minHeight: '100vh', borderRadius: '12px' }}>
      {/* Header */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
          <div>
            <button 
              onClick={() => navigate(`/code-repos/${repoId}/progress`)}
              className="btn btn-secondary"
              style={{ marginBottom: '12px', fontSize: '13px', padding: '6px 12px' }}
            >
              ← Back to Progress
            </button>
            <h2 style={{ color: '#667eea', fontSize: '24px', marginBottom: '8px' }}>
              {student?.name}'s Progress
            </h2>
            <p style={{ color: '#718096', fontSize: '14px', margin: 0 }}>
              Repository: {repo?.title} ({repo?.repoCode})
            </p>
          </div>
          
          <div style={{ textAlign: 'right' }}>
            <div style={{ 
              padding: '12px 20px', 
              background: '#f0fdf4', 
              borderRadius: '8px',
              border: '1px solid #86efac'
            }}>
              <div style={{ fontSize: '12px', color: '#15803d', marginBottom: '2px' }}>
                Overall Progress
              </div>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#22c55e' }}>
                {summary?.percentageComplete}%
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="card" style={{ marginBottom: '20px', padding: '20px' }}>
        <h3 style={{ fontSize: '16px', marginBottom: '16px', color: '#1a202c' }}>
          Summary
        </h3>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
          gap: '16px' 
        }}>
          <div>
            <div style={{ fontSize: '12px', color: '#718096', marginBottom: '4px' }}>
              Total Assignments
            </div>
            <div style={{ fontSize: '24px', fontWeight: '700', color: '#1a202c' }}>
              {summary?.totalAssignments}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '12px', color: '#718096', marginBottom: '4px' }}>
              Completed
            </div>
            <div style={{ fontSize: '24px', fontWeight: '700', color: '#22c55e' }}>
              {summary?.completed}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '12px', color: '#718096', marginBottom: '4px' }}>
              In Progress
            </div>
            <div style={{ fontSize: '24px', fontWeight: '700', color: '#3b82f6' }}>
              {summary?.inProgress}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '12px', color: '#718096', marginBottom: '4px' }}>
              Not Started
            </div>
            <div style={{ fontSize: '24px', fontWeight: '700', color: '#6b7280' }}>
              {summary?.notStarted}
            </div>
          </div>
        </div>
      </div>

      {/* Assignment Progress */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <h3 style={{ fontSize: '18px', marginBottom: '16px', color: '#1a202c', padding: '20px 20px 0' }}>
          Assignment Progress
        </h3>
        
        {assignmentProgress && assignmentProgress.length > 0 ? (
          <div style={{ overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ background: '#f7fafc', borderBottom: '2px solid #e2e8f0' }}>
                  <th style={{ padding: '12px 20px', textAlign: 'left', fontWeight: '600' }}>#</th>
                  <th style={{ padding: '12px 20px', textAlign: 'left', fontWeight: '600' }}>Assignment</th>
                  <th style={{ padding: '12px 20px', textAlign: 'center', fontWeight: '600' }}>Level</th>
                  <th style={{ padding: '12px 20px', textAlign: 'center', fontWeight: '600' }}>Status</th>
                  <th style={{ padding: '12px 20px', textAlign: 'center', fontWeight: '600' }}>Score</th>
                  <th style={{ padding: '12px 20px', textAlign: 'center', fontWeight: '600' }}>Steps</th>
                  <th style={{ padding: '12px 20px', textAlign: 'left', fontWeight: '600' }}>Dates</th>
                  <th style={{ padding: '12px 20px', textAlign: 'center', fontWeight: '600' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {assignmentProgress.map((item, index) => {
                  const statusInfo = getStatusColor(item.status);
                  const assignment = item.assignment;
                  const submission = item.submission;
                  
                  return (
                    <React.Fragment key={assignment._id}>
                      <tr 
                        style={{ 
                          borderBottom: '1px solid #e2e8f0',
                          background: index % 2 === 0 ? '#ffffff' : '#f9fafb'
                        }}
                      >
                      <td style={{ padding: '16px 20px', color: '#6b7280', fontWeight: '600' }}>
                        {index + 1}
                      </td>
                      <td style={{ padding: '16px 20px' }}>
                        <div style={{ fontWeight: '600', color: '#1a202c', marginBottom: '2px' }}>
                          {assignment.title}
                        </div>
                        <div style={{ fontSize: '12px', color: '#718096' }}>
                          {assignment.assignmentCode}
                        </div>
                      </td>
                      <td style={{ padding: '16px 20px', textAlign: 'center' }}>
                        <span style={{
                          padding: '4px 10px',
                          background: `${getLevelColor(assignment.level)}20`,
                          color: getLevelColor(assignment.level),
                          borderRadius: '6px',
                          fontSize: '12px',
                          fontWeight: '600',
                          textTransform: 'capitalize'
                        }}>
                          {assignment.level}
                        </span>
                      </td>
                      <td style={{ padding: '16px 20px', textAlign: 'center' }}>
                        <span style={{
                          padding: '6px 12px',
                          background: statusInfo.bg,
                          color: statusInfo.text,
                          borderRadius: '6px',
                          fontSize: '12px',
                          fontWeight: '600'
                        }}>
                          {statusInfo.label}
                        </span>
                      </td>
                      <td style={{ padding: '16px 20px', textAlign: 'center', fontWeight: '700', color: '#1a202c' }}>
                        {submission ? `${submission.totalScore || 0}%` : '-'}
                      </td>
                      <td style={{ padding: '16px 20px', textAlign: 'center', color: '#718096' }}>
                        {submission 
                          ? `${submission.currentStep} / ${submission.stepSubmissions?.length || 0}`
                          : '-'
                        }
                      </td>
                      <td style={{ padding: '16px 20px' }}>
                        {submission ? (
                          <div style={{ fontSize: '12px' }}>
                            {submission.startedAt && (
                              <div style={{ color: '#718096', marginBottom: '2px' }}>
                                Started: {new Date(submission.startedAt).toLocaleDateString()}
                              </div>
                            )}
                            {submission.completedAt && (
                              <>
                                <div style={{ color: '#22c55e', fontWeight: '600', marginBottom: '2px' }}>
                                  Completed: {new Date(submission.completedAt).toLocaleDateString()}
                                </div>
                                <div style={{ color: '#667eea', fontWeight: '600', fontSize: '11px', marginTop: '4px' }}>
                                  ⏱️ Time: {formatTimeTaken(submission.startedAt, submission.completedAt)}
                                </div>
                              </>
                            )}
                          </div>
                        ) : (
                          <span style={{ color: '#9ca3af', fontSize: '12px' }}>-</span>
                        )}
                      </td>
                      <td style={{ padding: '16px 20px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', alignItems: 'center' }}>
                          {submission && submission.stepSubmissions && submission.stepSubmissions.length > 0 && (
                            <button
                              onClick={() => toggleAssignmentExpanded(assignment._id)}
                              style={{
                                padding: '6px 12px',
                                background: '#667eea',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                fontSize: '12px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = '#5568d3';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = '#667eea';
                              }}
                            >
                              {expandedAssignments.has(assignment._id) ? '▼ Hide Code' : '📝 View Code'}
                            </button>
                          )}
                          {submission && !submission.isCompleted ? (
                            <button
                              onClick={() => handleMarkComplete(submission._id)}
                              disabled={markingComplete === submission._id}
                              style={{
                                padding: '6px 12px',
                                background: markingComplete === submission._id ? '#9ca3af' : '#22c55e',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                fontSize: '12px',
                                fontWeight: '600',
                                cursor: markingComplete === submission._id ? 'not-allowed' : 'pointer',
                                opacity: markingComplete === submission._id ? 0.6 : 1,
                                transition: 'all 0.2s'
                              }}
                              onMouseEnter={(e) => {
                                if (markingComplete !== submission._id) {
                                  e.currentTarget.style.background = '#16a34a';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (markingComplete !== submission._id) {
                                  e.currentTarget.style.background = '#22c55e';
                                }
                              }}
                            >
                              {markingComplete === submission._id ? 'Marking...' : '✓ Mark Complete'}
                            </button>
                          ) : submission?.isCompleted ? (
                            <span style={{ color: '#22c55e', fontSize: '12px', fontWeight: '600' }}>✓ Complete</span>
                          ) : (
                            <span style={{ color: '#9ca3af', fontSize: '12px' }}>-</span>
                          )}
                          {submission && (
                            <button
                              onClick={() => handleDeleteSubmission(submission._id)}
                              disabled={deletingSubmission === submission._id}
                              style={{
                                padding: '6px 12px',
                                background: deletingSubmission === submission._id ? '#9ca3af' : '#ef4444',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                fontSize: '12px',
                                fontWeight: '600',
                                cursor: deletingSubmission === submission._id ? 'not-allowed' : 'pointer',
                                opacity: deletingSubmission === submission._id ? 0.6 : 1,
                                transition: 'all 0.2s'
                              }}
                              onMouseEnter={(e) => {
                                if (deletingSubmission !== submission._id) {
                                  e.currentTarget.style.background = '#dc2626';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (deletingSubmission !== submission._id) {
                                  e.currentTarget.style.background = '#ef4444';
                                }
                              }}
                            >
                              {deletingSubmission === submission._id ? 'Deleting...' : '🗑️ Delete'}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {/* Expanded Code View */}
                    {expandedAssignments.has(assignment._id) && submission && submission.stepSubmissions && submission.stepSubmissions.length > 0 && (
                      <tr>
                        <td colSpan="8" style={{ padding: 0, background: '#f7fafc' }}>
                          <div style={{ padding: '20px', borderTop: '2px solid #e2e8f0' }}>
                            <h4 style={{ margin: '0 0 16px 0', fontSize: '15px', fontWeight: '600', color: '#1a202c' }}>
                              📝 Code Submissions
                            </h4>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                              {submission.stepSubmissions.map((stepSub, stepIdx) => (
                                <div
                                  key={stepIdx}
                                  style={{
                                    padding: '16px',
                                    backgroundColor: stepSub.isComplete ? '#f0fdf4' : '#fef2f2',
                                    borderRadius: '8px',
                                    border: `1px solid ${stepSub.isComplete ? '#86efac' : '#fecaca'}`
                                  }}
                                >
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                    <h5 style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#1a202c' }}>
                                      Step {stepSub.stepNumber}
                                    </h5>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                      <span style={{
                                        fontSize: '12px',
                                        color: '#718096',
                                        fontFamily: 'monospace',
                                        backgroundColor: 'white',
                                        padding: '4px 8px',
                                        borderRadius: '4px'
                                      }}>
                                        {stepSub.language}
                                      </span>
                                      <span style={{
                                        fontSize: '12px',
                                        fontWeight: '600',
                                        color: stepSub.isComplete ? '#22c55e' : '#ef4444'
                                      }}>
                                        {stepSub.isComplete ? '✓ Complete' : '✗ Incomplete'}
                                      </span>
                                    </div>
                                  </div>

                                  {stepSub.aiReview?.feedback && (
                                    <div style={{ marginBottom: '12px' }}>
                                      <div style={{ fontSize: '12px', fontWeight: '600', color: '#718096', marginBottom: '6px' }}>
                                        AI Feedback:
                                      </div>
                                      <div style={{
                                        padding: '10px',
                                        backgroundColor: 'white',
                                        borderRadius: '6px',
                                        fontSize: '13px',
                                        color: '#2d3748',
                                        border: '1px solid #e2e8f0'
                                      }}>
                                        {stepSub.aiReview.feedback}
                                      </div>
                                    </div>
                                  )}

                                  {stepSub.code ? (
                                    <div>
                                      <div style={{ fontSize: '12px', fontWeight: '600', color: '#718096', marginBottom: '6px' }}>
                                        Code:
                                      </div>
                                      <MemoizedSyntaxHighlighter
                                        language={stepSub.language || 'javascript'}
                                        style={vscDarkPlus}
                                        customStyle={{
                                          borderRadius: '6px',
                                          fontSize: '13px',
                                          margin: 0,
                                          maxHeight: '400px',
                                          overflow: 'auto'
                                        }}
                                        wrapLongLines={true}
                                      >
                                        {stepSub.code}
                                      </MemoizedSyntaxHighlighter>
                                    </div>
                                  ) : (
                                    <div style={{
                                      padding: '12px',
                                      backgroundColor: 'white',
                                      borderRadius: '6px',
                                      fontSize: '13px',
                                      color: '#a0aec0',
                                      textAlign: 'center',
                                      border: '1px solid #e2e8f0'
                                    }}>
                                      No code available
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <p style={{ fontSize: '36px', margin: '0 0 12px 0' }}>📝</p>
            <p style={{ color: '#718096', margin: 0 }}>No assignments in this repository</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default memo(StudentRepoProgress);
